// Fixed color mapping for common allergens - maximally distinguishable
const ALLERGEN_COLORS = {};

// Helper to add allergen with all Unicode normalizations
function addAllergen(name, color) {
  const lower = name.toLowerCase();
  ALLERGEN_COLORS[lower] = color;
  ALLERGEN_COLORS[lower.normalize('NFC')] = color;
  ALLERGEN_COLORS[lower.normalize('NFD')] = color;
}

// English allergens
addAllergen('gluten', '#E41A1C');        // bright red
addAllergen('egg', '#377EB8');           // strong blue
addAllergen('eggs', '#377EB8');          // strong blue
addAllergen('milk', '#4DAF4A');          // green
addAllergen('dairy', '#4DAF4A');         // green
addAllergen('nuts', '#FF7F00');          // orange
addAllergen('peanuts', '#984EA3');       // purple
addAllergen('soy', '#FFDD33');           // yellow
addAllergen('soya', '#FFDD33');          // yellow
addAllergen('fish', '#00CED1');          // turquoise
addAllergen('shellfish', '#F781BF');     // pink
addAllergen('sesame', '#A65628');        // brown
addAllergen('celery', '#00FF7F');        // spring green
addAllergen('mustard', '#FFD700');       // gold
addAllergen('sulphites', '#8B008B');     // dark magenta
addAllergen('lupin', '#1E90FF');         // dodger blue
addAllergen('molluscs', '#FF6347');      // tomato

// Bulgarian (Cyrillic) allergens
addAllergen('глутен', '#E41A1C');        // bright red
addAllergen('яйце', '#377EB8');          // strong blue
addAllergen('яйца', '#377EB8');          // strong blue
addAllergen('мляко', '#4DAF4A');         // green
addAllergen('ядки', '#FF7F00');          // orange
addAllergen('фъстъци', '#984EA3');       // purple
addAllergen('соя', '#FFDD33');           // yellow
addAllergen('риба', '#00CED1');          // turquoise
addAllergen('ракообразни', '#F781BF');   // pink
addAllergen('сусам', '#A65628');         // brown
addAllergen('целина', '#00FF7F');        // spring green
addAllergen('горчица', '#FFD700');       // gold
addAllergen('сулфити', '#8B008B');       // dark magenta
addAllergen('лупина', '#1E90FF');        // dodger blue
addAllergen('мекотели', '#FF6347');      // tomato

// Fallback palette for unknown allergens
const FALLBACK_PALETTE = [
  '#E41A1C', '#377EB8', '#4DAF4A', '#FF7F00', '#984EA3',
  '#FFDD33', '#A65628', '#F781BF', '#00CED1', '#00FF7F',
  '#FFD700', '#8B008B', '#1E90FF', '#FF6347',
];

// Track assigned colors for unknown allergens
let unknownIndex = 0;
const unknownCache = new Map();

// Get color for an allergen
export function getAllergenColor(allergen) {
  if (!allergen) return '#808080';

  const lowered = allergen.toLowerCase().trim();

  // Try exact match
  if (ALLERGEN_COLORS[lowered]) {
    return ALLERGEN_COLORS[lowered];
  }

  // Try NFC normalized (precomposed - е.g. й as single char)
  const nfc = lowered.normalize('NFC');
  if (ALLERGEN_COLORS[nfc]) {
    return ALLERGEN_COLORS[nfc];
  }

  // Try NFD normalized (decomposed - e.g. й as и + combining breve)
  const nfd = lowered.normalize('NFD');
  if (ALLERGEN_COLORS[nfd]) {
    return ALLERGEN_COLORS[nfd];
  }

  // Try without combining marks (for Latin diacritics)
  const stripped = nfd.replace(/[\u0300-\u036f]/g, '');
  if (ALLERGEN_COLORS[stripped]) {
    return ALLERGEN_COLORS[stripped];
  }

  // Check if we've already assigned a color to this unknown allergen
  if (unknownCache.has(nfc)) {
    return unknownCache.get(nfc);
  }

  // Assign next available color from fallback palette
  const color = FALLBACK_PALETTE[unknownIndex % FALLBACK_PALETTE.length];
  unknownIndex++;
  unknownCache.set(nfc, color);
  return color;
}

// Get all unique allergens with their colors from a list of products
export function buildAllergenLegend(products) {
  const allergenSet = new Map();

  for (const product of products) {
    if (product.allergens && Array.isArray(product.allergens)) {
      for (const variantAllergens of product.allergens) {
        if (Array.isArray(variantAllergens)) {
          for (const allergen of variantAllergens) {
            const key = allergen?.toLowerCase().trim();
            if (key && !allergenSet.has(key)) {
              allergenSet.set(key, {
                name: allergen,
                color: getAllergenColor(allergen),
              });
            }
          }
        }
      }
    }
  }

  return Array.from(allergenSet.values());
}

export default { getAllergenColor, buildAllergenLegend };
